import React, { useState, useEffect, createContext, useRef, useContext } from "react"
import { Table, Modal, Button, message, Form, DatePicker, InputNumber, Input, Space } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import { API } from "../utils/API"
import moment from "moment"

const EditableContext = createContext(null)

const { Column } = Table

const OrdersTable = (props) => {
	const [orderDownloads, setOrderDownloads] = useState([])

	const searchInput = useRef(null)

	const uniqueProducts = [...new Set(props.orders.map((order) => order.product))]
	const filters = uniqueProducts.map((product) => ({
		text: product,
		value: product,
	}))

	const getOrderDownloads = (order_id) => {
		API.getDownloads({ order_id }).then((res) => {
			if (res.data.length > 0) setOrderDownloads(res.data)
			else message.error("There are no downloads for this order")
		})
	}
	const deleteOrder = (order_id) => {
		Modal.confirm({
			title: "Confirm delete",
			content: "Do you want to delete this order?",
			okText: "Yes",
			cancelText: "No",
			onOk: () => {
				API.deleteOrder(order_id).then((res) => {
					if (res.data.length > 0) {
						API.getOrders().then((res) => props.setOrders(res.data))
						message.success("Order deleted")
					} else message.error("There are no downloads for this order")
				})
			},
		})
	}
	const updateLocalData = (row) => {
		const newData = [...props.orders]
		const index = newData.findIndex((item) => row.id === item.id)
		const item = newData[index]
		newData.splice(index, 1, { ...item, ...row })
		props.setOrders(newData)
		message.success("Order Updated Successfully")
	}

	const handleSave = (row) => {
		const { id, downloads_available } = row
		API.updateOrderDownloads({ id, downloads_available }).then((res) => {
			if (!res.ok) message.error("There was a problem updating this record")
			else updateLocalData(row)
		})
	}

	const handleDateChange = (row, download_expiration) => {
		const { id } = row
		row.download_expiration = download_expiration
		API.updateOrderExpiration({ id, download_expiration }).then((res) => {
			if (!res.ok) message.error("There was a problem updating this record")
			else updateLocalData(row)
		})
	}

	const EditableRow = ({ index, ...props }) => {
		const [form] = Form.useForm()
		return (
			<Form form={form} component={false}>
				<EditableContext.Provider value={form}>
					<tr {...props} />
				</EditableContext.Provider>
			</Form>
		)
	}
	const EditableCell = ({ title, editable, type, children, dataIndex, record, handleSave, ...restProps }) => {
		const [editing, setEditing] = useState(false)
		const inputRef = useRef(null)
		const form = useContext(EditableContext)
		useEffect(() => {
			if (editing) {
				inputRef?.current?.focus()
			}
		}, [editing])

		const toggleEdit = () => {
			setEditing(!editing)
			form.setFieldsValue({
				[dataIndex]: record[dataIndex],
			})
		}

		const save = async () => {
			try {
				const values = await form.validateFields()
				toggleEdit()
				handleSave({ ...record, ...values })
			} catch (errInfo) {
				console.log("Save failed:", errInfo)
			}
		}

		let childNode = children

		if (editable && type === "input") {
			childNode = editing ? (
				<Form.Item
					style={{
						margin: 0,
					}}
					name={dataIndex}
					rules={[
						{
							required: true,
							message: `${title} is required.`,
						},
					]}
				>
					<InputNumber ref={inputRef} onBlur={save} onPressEnter={save} />
				</Form.Item>
			) : (
				<div className="editable-cell-value-wrap" onClick={toggleEdit}>
					{children}
				</div>
			)
		}

		if (editable && type === "date") {
			childNode = editing ? (
				<DatePicker
					autoFocus
					open
					allowClear={false}
					onChange={(date) => handleDateChange(record, date)}
					format={"MM/DD/YYYY"}
				/>
			) : (
				<div className="editable-cell-value-wrap" onClick={toggleEdit}>
					{children}
				</div>
			)
		}

		return <td {...restProps}>{childNode}</td>
	}

	return (
		<>
			<Table
				dataSource={props.orders}
				components={{
					body: {
						row: EditableRow,
						cell: EditableCell,
					},
				}}
				rowClassName={() => "editable-row"}
			>
				<Column
					title="Order Created"
					dataIndex="created"
					key="created"
					render={(text) => moment(text).format("MM/DD/YYYY")}
					sorter={(a, b) => moment(a.created).unix() - moment(b.created).unix()}
				/>
				<Column
					title="Product"
					dataIndex="product"
					key="product"
					filters={filters}
					onFilter={(value, record) => record.product?.indexOf(value) === 0}
					sorter={(a, b) => a.product.localeCompare(b.product)}
				/>
				<Column
					title="Order Number"
					dataIndex="order_number"
					key="order_number"
					ellipsis={false}
					width="100"
					sorter={(a, b) => a.order_number.localeCompare(b.order_number)}
					filterDropdown={({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
						<div style={{ padding: 8 }}>
							<Input
								ref={searchInput}
								placeholder={`Search Order Number`}
								value={selectedKeys[0]}
								onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
								onPressEnter={() => confirm()}
								style={{ marginBottom: 8, display: "block" }}
							/>
							<Space>
								<Button
									onClick={() => {
										clearFilters()
										confirm()
									}}
									size="small"
									style={{ width: 90 }}
								>
									Reset
								</Button>
								<Button
									type="primary"
									onClick={() => confirm()}
									icon={<SearchOutlined />}
									size="small"
									style={{ width: 90 }}
								>
									Search
								</Button>
							</Space>
						</div>
					)}
					filterIcon={(filtered) => (
						<SearchOutlined
							style={{
								color: filtered ? "#1890ff" : undefined,
							}}
						/>
					)}
					onFilter={(value, record) => (record.order_number ? record.order_number.toString().includes(value) : "")}
					onFilterDropdownVisibleChange={(visible) => {
						if (visible) {
							setTimeout(() => searchInput.current?.select(), 100)
						}
					}}
				/>
				<Column
					title="Product Serial Number"
					dataIndex="access_code"
					key="access_code"
					sorter={(a, b) => a.access_code.localeCompare(b.access_code)}
					filterDropdown={({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
						<div style={{ padding: 8 }}>
							<Input
								ref={searchInput}
								placeholder={`Search Serial Number`}
								value={selectedKeys[0]}
								onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
								onPressEnter={() => confirm()}
								style={{ marginBottom: 8, display: "block" }}
							/>
							<Space>
								<Button
									onClick={() => {
										clearFilters()
										confirm()
									}}
									size="small"
									style={{ width: 90 }}
								>
									Reset
								</Button>
								<Button
									type="primary"
									onClick={() => confirm()}
									icon={<SearchOutlined />}
									size="small"
									style={{ width: 90 }}
								>
									Search
								</Button>
							</Space>
						</div>
					)}
					filterIcon={(filtered) => (
						<SearchOutlined
							style={{
								color: filtered ? "#1890ff" : undefined,
							}}
						/>
					)}
					onFilter={(value, record) => (record.access_code ? record.access_code.toString().includes(value) : "")}
					onFilterDropdownVisibleChange={(visible) => {
						if (visible) {
							setTimeout(() => searchInput.current?.select(), 100)
						}
					}}
				/>

				<Column
					title="Download Expiration"
					dataIndex="download_expiration"
					key="download_expiration"
					render={(text) => moment(text).format("MM/DD/YYYY")}
					sorter={(a, b) => moment(a.download_expiration).unix() - moment(b.download_expiration).unix()}
					onCell={(record) => ({
						record,
						editable: true,
						type: "date",
						dataIndex: "downloads_available",
						title: "Downloads Available",
						handleSave,
					})}
				/>
				<Column
					title="Downloads Available"
					dataIndex="downloads_available"
					key="downloads_available"
					sorter={(a, b) => a.downloads_available - b.downloads_available}
					onCell={(record) => ({
						record,
						editable: true,
						type: "input",
						dataIndex: "downloads_available",
						title: "Downloads Available",
						handleSave,
					})}
				/>
				<Column title="Downloads Complete" dataIndex="downloads_complete" key="downloads_complete" />
				<Column
					title="Download History"
					dataIndex="id"
					key="id"
					render={(text) => (
						<Button type="link" onClick={() => getOrderDownloads(text)}>
							View Logs
						</Button>
					)}
				/>
				<Column
					title=""
					dataIndex="id"
					key="id"
					render={(text) => (
						<Button type="danger" onClick={() => deleteOrder(text)}>
							Delete
						</Button>
					)}
				/>
			</Table>

			<Modal
				title="Order Downloads"
				visible={orderDownloads.length > 0}
				footer={null}
				onCancel={() => setOrderDownloads([])}
			>
				<Table dataSource={orderDownloads}>
					<Column title="Date" dataIndex="created" key="created" render={(text) => moment(text).format("MM/DD/YYYY")} />
					<Column
						title="Download Complete"
						dataIndex="download_complete"
						key="download_complete"
						render={(text) => (text === 0 ? "No" : "Yes")}
					/>
					<Column title="Country" dataIndex="country" key="country" />
					<Column
						title="IPStack"
						dataIndex="ipstack"
						key="ipstack"
						render={(text) => (
							<Button
								href={`data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(text, null, 4))}`}
								download="ipstack.json"
								type="link"
							>
								{`Download IPStack`}
							</Button>
						)}
					/>
				</Table>
			</Modal>
		</>
	)
}

export default OrdersTable
